import { useState } from 'react';
import { useApi } from '@/api/useApi';
import { useMutationProvider } from '@/hooks/use-mutation-provider';

interface UploadInfo {
	baseUrl: string;
	path: string;
	algorithm: string;
	date: string;
	signedHeaders: string;
	expires: string;
	credential: string;
	signature: string;
	filename: string;
}

interface UploadState {
	isUploading: boolean;
	progress: number;
	error: string | null;
	success: boolean;
}

interface UploadResult {
	path: string;
	filename: string;
	filePath: string;
}

interface MeetingFormData {
	recordingFileName: string;
	fileExtension: string;
}

const INITIAL_UPLOAD_STATE: UploadState = {
	isUploading: false,
	progress: 0,
	error: null,
	success: false
};

const ERROR_MESSAGES = {
	UPLOAD_INFO_FAILED: 'Failed to get upload info',
	S3_UPLOAD_FAILED: 'Failed to upload to S3',
	UPLOAD_FAILED: 'Upload failed',
	UPDATE_FAILED: 'Document update failed'
} as const;

const buildS3UploadUrl = (uploadInfo: UploadInfo): string => {
	const baseUrl = `${uploadInfo.baseUrl}/${uploadInfo.path}/${uploadInfo.filename}`;
	const params = [
		`X-Amz-Algorithm=${uploadInfo.algorithm}`,
		`X-Amz-Credential=${uploadInfo.credential}`,
		`X-Amz-Date=${uploadInfo.date}`,
		`X-Amz-Expires=${uploadInfo.expires}`,
		`X-Amz-SignedHeaders=${uploadInfo.signedHeaders}`,
		`X-Amz-Signature=${uploadInfo.signature}`
	].join('&');

	return `${baseUrl}?${params}`;
};

const extractErrorMessage = (error: unknown, fallback: string): string => {
	return error instanceof Error ? error.message : fallback;
};

export const useMeetingVideoUpload = ({ interactionId }: { interactionId: number }) => {
	const [uploadState, setUploadState] = useState<UploadState>(INITIAL_UPLOAD_STATE);
	const { InteractionControllerApi } = useApi();

	const getUploadInfoMutation = useMutationProvider(() => InteractionControllerApi.generateUploadInfoLink(), {
		onError: (error: Error) => {
			setUploadState(prev => ({
				...prev,
				error: error?.message || ERROR_MESSAGES.UPLOAD_FAILED,
				isUploading: false
			}));
		}
	});

	const updateMeetingFormMutation = useMutationProvider(
		async ({ externalReportId, formData }: { externalReportId: number; formData: MeetingFormData }) => {
			return await InteractionControllerApi.updateInteractionExternalReportById(interactionId, externalReportId, formData);
		},
		{
			onError: (error: Error) => {
				setUploadState(prev => ({
					...prev,
					error: error?.message || ERROR_MESSAGES.UPDATE_FAILED,
					isUploading: false
				}));
			}
		}
	);

	const uploadToS3 = async (file: File, uploadInfo: UploadInfo): Promise<UploadResult | null> => {
		try {
			const uploadUrl = buildS3UploadUrl(uploadInfo);
			const response = await fetch(uploadUrl, {
				method: 'PUT',
				body: file
			});

			if (!response.ok) {
				console.error(`S3 upload failed: ${response.statusText}`);
				return null;
			}

			return {
				path: uploadInfo.path,
				filename: uploadInfo.filename,
				filePath: `${uploadInfo.path}/${uploadInfo.filename}`
			};
		} catch (error) {
			console.error('Error uploading to S3:', error);
			return null;
		}
	};

	const getInfoAndUploadToS3 = async (file: File, externalReportId: number): Promise<UploadResult | null> => {
		try {
			setUploadState(prev => ({
				...prev,
				isUploading: true,
				progress: 0
			}));

			const uploadInfoResponse = await getUploadInfoMutation.mutateAsync();
			const uploadInfo = uploadInfoResponse?.data?.data;

			if (!uploadInfo) {
				setUploadState(prev => ({
					...prev,
					error: ERROR_MESSAGES.UPLOAD_INFO_FAILED,
					isUploading: false
				}));
				return null;
			}

			const uploadResult = await uploadToS3(file, uploadInfo);

			if (!uploadResult) {
				setUploadState(prev => ({
					...prev,
					error: ERROR_MESSAGES.S3_UPLOAD_FAILED,
					isUploading: false
				}));
				return null;
			}

			const fileExtension = file.name.split('.').pop() || '';

			await updateMeetingFormMutation.mutateAsync({
				externalReportId,
				formData: { recordingFileName: uploadInfo.filename + '.' + fileExtension }
			});

			setUploadState(prev => ({
				...prev,
				isUploading: false,
				progress: 100,
				success: true
			}));

			return uploadResult;
		} catch (error) {
			console.error('Error in getInfoAndUploadToS3:', error);
			setUploadState(prev => ({
				...prev,
				error: extractErrorMessage(error, ERROR_MESSAGES.UPLOAD_FAILED),
				isUploading: false
			}));
			return null;
		}
	};

	const resetUploadState = () => {
		setUploadState(INITIAL_UPLOAD_STATE);
	};

	return {
		getInfoAndUploadToS3,
		uploadToS3,
		resetUploadState,
		uploadState,
		isLoading: getUploadInfoMutation.isLoading || uploadState.isUploading
	};
};
